import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
  faEye,
  faTrash,
  faEnvelope,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../../common/RefreshButton";
import { getConfidenceDescription } from "../utils/confidenceUtils";
import {
  decodeQuotedPrintable,
  parseZuluTime,
  formatDateOnly,
  formatTimeOnly,
  parseRiskFactors,
} from "../utils/emailAnalysisUtils";
import EmailAnalysisAPI from "../../../services/EmailAnalysisAPI";
import EmptyStatePanel from "../../common/EmptyStatePanel";
import { FontAwesomeIcon as FAIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import "../css/EmailAnalysisSimple.css";

const AnalysisResultsSimple = ({
  rawAnalyses,
  selectedAnalysis,
  onAnalysisClick,
  onRefresh,
  userUuid,
  onError,
  onAnalysisDeleted,
  isMobile = false,
  onNavigateToManual,
}) => {
  const [analyses, setAnalyses] = useState([]);
  const { t } = useTranslation();

  // Utility function to decode email content (similar to AnalysisDetailsSimple)
  const decodeEmailContent = (content) => {
    if (!content) return "";

    try {
      let decoded = content;

      // Handle Quoted-Printable encoding
      if (content.includes("=")) {
        try {
          decoded = content
            .replace(/=\r?\n/g, "")
            .replace(/=([0-9A-F]{2})/gi, (match, hex) => {
              return String.fromCharCode(parseInt(hex, 16));
            });
        } catch (e) {
          console.warn("Error decoding quoted-printable:", e);
        }
      }

      // Handle URL encoding
      if (decoded.includes("%")) {
        try {
          decoded = decodeURIComponent(decoded);
        } catch (e) {
          try {
            decoded = decodeURIComponent(escape(decoded));
          } catch (e2) {
            console.warn("Error with URL decoding:", e2);
          }
        }
      }

      // Clean up email content
      decoded = decoded
        .replace(
          /^(From|To|Subject|Date|Message-ID|Return-Path|Received|MIME-Version|Content-Type|Content-Transfer-Encoding|X-[\w-]+):.*$/gim,
          "",
        )
        .replace(/--[\w-]+/g, "")
        .replace(/Content-Type:.*$/gim, "")
        .replace(/Content-Transfer-Encoding:.*$/gim, "")
        .replace(/charset=[\w-]+/gi, "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, " ")
        .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "")
        .replace(/[\u034F]/g, "")
        .replace(/[\u061C]/g, "")
        .replace(/[\u180E]/g, "")
        .replace(/\n\s*\n\s*\n/g, "\n\n")
        .replace(/[ \t]+/g, " ")
        .replace(/^\s+|\s+$/g, "");

      if (!/<[^>]+>/.test(decoded)) {
        decoded = decoded
          .replace(/\n/g, "<br>")
          .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
      }

      return decoded;
    } catch (error) {
      console.warn("Error decoding email content:", error);
      return content || "";
    }
  };

  // Helper function to determine threat type display based on confidence
  const getThreatTypeDisplay = (scamType, confidence) => {
    if (confidence <= 0.2) {
      return "None";
    }
    return scamType || "UNKNOWN";
  };

  // Data transformation functions
  const transformAnalysisData = (analysisData) => {
    return analysisData.map((item) => {
      // 🔍 DEBUG: Log what we receive from backend
      console.log("🔍 TIMESTAMP-DEBUG: Frontend received item:", {
        key: item.analysisKey,
        analysisResult: item.analysisResult,
        analyzedAt: item.analysisResult?.analyzedAt,
        allFields: Object.keys(item.analysisResult || {}),
      });

      const analysisTimestamp = item.analysisResult?.analyzedAt;
      console.log(
        "🔍 TIMESTAMP-DEBUG: Using timestamp:",
        analysisTimestamp,
        "parsed:",
        parseZuluTime(analysisTimestamp),
      );

      return {
        key: item.analysisKey,
        lastModified: parseZuluTime(analysisTimestamp),
        size: 0,
        originalKey: item.originalKey,
        originalEmailContent: item.analysisResult?.analyzedEmailContent
          ? item.analysisResult.analyzedEmailContent.body || "No body content"
          : item.originalEmailContent,
        analysis: {
          analysisResult: {
            isScam: item.analysisResult?.isScam || false,
            confidence: item.analysisResult?.confidence || 0,
            scamType: getThreatTypeDisplay(
              item.analysisResult?.scamType,
              item.analysisResult?.confidence || 0,
            ),
            reasoning: item.analysisResult?.explanation || "",
            sender:
              item.analysisResult?.analyzedEmailContent?.senderEmail ||
              item.analysisResult?.sender ||
              "Unknown",
            subject:
              item.analysisResult?.analyzedEmailContent?.subject ||
              item.analysisResult?.subject ||
              "No subject",
            analysisDate: parseZuluTime(analysisTimestamp),
            suspiciousElements: parseRiskFactors(
              item.analysisResult?.riskFactors,
            ),
            suggestions: item.analysisResult?.suggestions || "",
          },
          analysisTimestamp: parseZuluTime(analysisTimestamp),
          analysisMethod: "BACKEND_API",
        },
      };
    });
  };

  const sortAnalysesByDate = (data) => {
    return data.sort((a, b) => {
      const getTimestamp = (item) => {
        // Prioritize analysis timestamp from analysisResult.analyzedAt
        const analysisTime =
          item.analysis?.analysisResult?.analysisDate ||
          item.analysis?.analysisTimestamp ||
          item.lastModified;

        let timestamp = 0;
        if (analysisTime instanceof Date) {
          timestamp = analysisTime.getTime();
        } else {
          // Parse the timestamp and convert to local time
          const parsedTime = parseZuluTime(analysisTime);
          timestamp = parsedTime ? parsedTime.getTime() : 0;
        }

        console.log("AnalysisResultsSimple: Item timestamp:", {
          key: item.key,
          analysisDate: item.analysis?.analysisResult?.analysisDate,
          analysisTimestamp: item.analysis?.analysisTimestamp,
          lastModified: item.lastModified,
          finalTimestamp: timestamp,
          finalDate: new Date(timestamp),
        });

        return timestamp;
      };

      const timeA = getTimestamp(a);
      const timeB = getTimestamp(b);

      // Sort in descending order (latest first) - more recent timestamps get higher priority
      return timeB - timeA;
    });
  };

  // Event handlers
  const handleDeleteAnalysis = async (analysisItem) => {
    try {
      await EmailAnalysisAPI.deleteAnalysis(analysisItem.key);

      // Check if the deleted analysis is currently selected in the details component
      if (selectedAnalysis && selectedAnalysis.key === analysisItem.key) {
        // Reset the details component by clearing the selected analysis
        onAnalysisClick(null);
      }

      onAnalysisDeleted(analysisItem.key);
    } catch (err) {
      console.error("AnalysisResultsSimple: Error deleting analysis:", err);
      onError(t("email.error.deleteFailed"));
    }
  };

  // Effects
  useEffect(() => {
    if (rawAnalyses && rawAnalyses.length > 0) {
      console.log("AnalysisResultsSimple: Raw analyses received:", rawAnalyses);
      // Data is already transformed in EmailAnalysisSimple, so just use it directly
      console.log("AnalysisResultsSimple: Using pre-transformed data");

      const sortedData = sortAnalysesByDate(rawAnalyses);
      console.log("AnalysisResultsSimple: Sorted data:", sortedData);

      // Show only the latest 5 items in simple view
      const latest5 = sortedData.slice(0, 5);
      console.log("AnalysisResultsSimple: Latest 5 items:", latest5);
      console.log(
        "AnalysisResultsSimple: Final order (latest first):",
        latest5.map((item) => ({
          key: item.key,
          timestamp:
            item.analysis?.analysisResult?.analysisDate || item.lastModified,
          subject: item.analysis?.analysisResult?.subject,
        })),
      );

      setAnalyses(latest5);
    } else {
      setAnalyses([]);
    }
  }, [rawAnalyses]);

  return (
    <>
      <div className="common-section-header">
        <div className="results-header-content">
          <div className="results-header-top">
            <h3>
              <FontAwesomeIcon icon={faClockRotateLeft} />{" "}
              {t("email.results.heading")}
            </h3>
            <RefreshButton
              onClick={onRefresh}
              title={t("email.results.refreshTitle")}
            />
          </div>
          <div className="results-header-bottom">
            <p>
              {t("email.results.showing").replace(
                "{count}",
                Math.min(5, analyses.length),
              )}
            </p>
          </div>
        </div>
      </div>
      <div className="panel-content history-panel-content">
        {analyses.length === 0 ? (
          <div className="empty-state-with-manual">
            <EmptyStatePanel
              icon={faInfoCircle}
              title={t("email.empty.noResults.title")}
              description={t("email.empty.noResults.description")}
              className="coming-soon-message"
            />
            {onNavigateToManual && (
              <div className="manual-entry-cta">
                <span className="manual-entry-cta-divider">— or —</span>
                <button
                  type="button"
                  className="common-button manual-entry-cta-btn"
                  onClick={onNavigateToManual}
                >
                  <FAIcon icon={faEdit} className="icon-right-margin" />
                  {t("email.empty.analyzeManuallyBtn")}
                </button>
              </div>
            )}
          </div>
        ) : (
          analyses.map((item) => (
            <AnalysisItemSimple
              key={item.key}
              item={item}
              isSelected={selectedAnalysis?.key === item.key}
              onClick={() => onAnalysisClick(item)}
              onDeleteAnalysis={handleDeleteAnalysis}
              formatDateOnly={formatDateOnly}
              formatTimeOnly={formatTimeOnly}
              decodeQuotedPrintable={decodeQuotedPrintable}
            />
          ))
        )}
      </div>
    </>
  );
};

const AnalysisItemSimple = ({
  item,
  isSelected,
  onClick,
  onDeleteAnalysis,
  formatDateOnly,
  formatTimeOnly,
  decodeQuotedPrintable,
}) => {
  const { t } = useTranslation();
  const handleDelete = (e) => {
    e.stopPropagation();
    onDeleteAnalysis(item);
  };

  return (
    <div
      className={`analysis-item-card ${isSelected ? "selected" : ""} ${
        item.analysis?.analysisResult?.isScam ? "scam" : ""
      }`}
      onClick={onClick}
    >
      {/* Top row: Status badge and Delete button */}
      <div className="analysis-item-header">
        <div
          className={`status-badge ${
            item.analysis?.analysisResult?.isScam ? "scam" : "legitimate"
          }`}
        >
          {item.analysis?.analysisResult?.isScam ? (
            <>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="fa-icon-margin-right-4"
              />
              {t("email.badge.scam")}
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="fa-icon-margin-right-4"
              />
              {t("email.badge.legitimate")}
            </>
          )}
        </div>

        <button
          className="delete-button"
          onClick={handleDelete}
          title={t("email.card.deleteTitle")}
        >
          {t("email.card.delete")}
        </button>
      </div>

      {/* Second row: From field */}
      <div className="analysis-item-row">
        <span className="field-label">{t("email.card.fromLabel")}</span>
        <span className="field-value">
          {item.analysis?.analysisResult?.sender || t("email.card.unknown")}
        </span>
      </div>

      {/* Third row: Subject field */}
      <div className="analysis-item-row">
        <span className="field-label">{t("email.card.subjectLabel")}</span>
        <span className="field-value">
          {decodeQuotedPrintable(item.analysis?.analysisResult?.subject) ||
            t("email.card.noSubject")}
        </span>
      </div>

      {/* Bottom row: Confidence and Date */}
      <div className="analysis-item-footer">
        <span className="confidence-info">
          {getConfidenceDescription(
            item.analysis?.analysisResult?.confidence || 0,
            item.analysis?.analysisResult?.isScam,
          )}
        </span>
        <span className="date-info">
          {formatDateOnly(
            item.analysis?.analysisResult?.analysisDate || item.lastModified,
          )}{" "}
          {formatTimeOnly(
            item.analysis?.analysisResult?.analysisDate || item.lastModified,
          )}
        </span>
      </div>
    </div>
  );
};

export default AnalysisResultsSimple;
